/**
 * Slimme afbeeldingscompressie utility
 * Comprimeert afbeeldingen op basis van hun grootte om optimale balans tussen kwaliteit en bestandsgrootte te behalen
 */

// Detecteert HEIC/HEIF-bestanden. De browser geeft voor .heic vaak een lege
// of incorrecte MIME-type terug, dus we controleren ook de extensie.
export const isHeicFile = (file) => {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  if (type === "image/heic" || type === "image/heif") return true;
  const name = (file.name || "").toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
};

// Converteert een HEIC/HEIF-bestand naar een JPEG-File. Desktopbrowsers
// (Chrome, Edge, Firefox) kunnen HEIC niet decoderen, dus zonder deze stap
// faalt zowel de canvas-compressie als het tonen van de afbeelding.
// Geeft het originele bestand ongewijzigd terug als het geen HEIC is.
export const convertHeicIfNeeded = async (file) => {
  if (!isHeicFile(file)) return file;

  // Lazy import zodat de (grote) library niet in de hoofdbundle terechtkomt.
  const heic2any = (await import("heic2any")).default;
  const convertedBlob = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.92,
  });
  // heic2any kan een array teruggeven bij meerdere afbeeldingen in 1 bestand.
  const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

  const newName = (file.name || "image").replace(/\.(heic|heif)$/i, ".jpg");
  return new File([blob], newName, {
    type: "image/jpeg",
    lastModified: file.lastModified || Date.now(),
  });
};

export const smartCompressImage = async (file) => {
  // HEIC/HEIF eerst naar JPEG converteren, anders kan de browser het niet
  // op een canvas tekenen en faalt de compressie. Idempotent: niet-HEIC
  // bestanden worden ongewijzigd teruggegeven.
  file = await convertHeicIfNeeded(file);

  return new Promise((resolve, reject) => {
    // Controleer bestandstype
    if (!file.type.startsWith("image/")) {
      reject(new Error("Alleen afbeeldingen zijn toegestaan"));
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");

          // Bepaal compressie instellingen gebaseerd op bestandsgrootte
          const fileSizeMB = file.size / (1024 * 1024);
          let quality = 0.9; // Standaard hoge kwaliteit
          let maxDimension = 2000; // Standaard maximale dimensie

          // Slimme compressie: pas instellingen aan op basis van bestandsgrootte
          if (fileSizeMB <= 2) {
            // Kleine bestanden: minimale compressie
            quality = 0.95;
            maxDimension = 2500;
          } else if (fileSizeMB <= 5) {
            // Middelgrote bestanden: lichte compressie
            quality = 0.85;
            maxDimension = 2000;
          } else if (fileSizeMB <= 10) {
            // Grote bestanden: matige compressie
            quality = 0.75;
            maxDimension = 1600;
          } else if (fileSizeMB <= 20) {
            // Zeer grote bestanden: meer compressie
            quality = 0.65;
            maxDimension = 1400;
          } else {
            // Extreem grote bestanden: sterke compressie
            quality = 0.55;
            maxDimension = 1200;
          }

          let width = img.width;
          let height = img.height;

          // Schaal afbeelding down als deze te groot is
          if (width > height) {
            if (width > maxDimension) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");

          // Verbeterde rendering voor betere kwaliteit
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, width, height);

          // Gebruik JPEG voor betere compressie van grote bestanden, behoud origineel type voor kleine bestanden
          const outputType = fileSizeMB > 2 ? "image/jpeg" : file.type;
          const dataUrl = canvas.toDataURL(outputType, quality);

          // Log compressie resultaten voor debugging
          const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          const compressedSizeMB = (
            (dataUrl.length * 0.75) /
            (1024 * 1024)
          ).toFixed(2);
          const compressionRatio = (
            (1 - (dataUrl.length * 0.75) / file.size) *
            100
          ).toFixed(1);

          console.log(`📷 Afbeelding compressie:
            Origineel: ${originalSizeMB}MB (${img.width}x${img.height})
            Gecomprimeerd: ${compressedSizeMB}MB (${width}x${height})
            Compressie: ${compressionRatio}%
            Kwaliteit: ${(quality * 100).toFixed(0)}%`);

          resolve(dataUrl);
        } catch (error) {
          reject(new Error(`Compressie mislukt: ${error.message}`));
        }
      };
      img.onerror = () => {
        reject(new Error("Kon afbeelding niet laden"));
      };
    };
    reader.onerror = () => {
      reject(new Error("Kon bestand niet lezen"));
    };
  });
};

// Zet een data-URL (resultaat van smartCompressImage) om naar een File,
// zodat hij via de multipart-upload (uploadMedia) kan i.p.v. de base64-route.
export const dataUrlToFile = (dataUrl, filename = "image.jpg") => {
  const [header, base64] = dataUrl.split(",");
  const mimeMatch = /data:([^;]+)/.exec(header || "");
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const binary = atob(base64 || "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  const ext =
    mime === "image/png" ? ".png" : mime === "image/webp" ? ".webp" : ".jpg";
  const base = (filename || "image").replace(/\.[^.]+$/, "");
  return new File([bytes], `${base}${ext}`, { type: mime });
};

// Helper functie om te controleren of een afbeelding compressie nodig heeft
export const needsCompression = (file, maxSizeMB = 2) => {
  return file.size > maxSizeMB * 1024 * 1024;
};

// Helper functie om bestandsgrootte in MB te krijgen
export const getFileSizeMB = (file) => {
  return (file.size / (1024 * 1024)).toFixed(2);
};
